import { useEffect, useState } from 'react';

const PREFIX = 'tkf.flags.' as const;

function readFlag(key: string, defaultValue: boolean): boolean {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const raw = window.localStorage.getItem(`${PREFIX}${key}`);
    if (raw === null) return defaultValue;
    return raw === 'true' || raw === '1';
  } catch {
    return defaultValue;
  }
}

/**
 * useLocalFlag — opt-in feature flag stored in localStorage.
 *
 * Devs / QA toggle in DevTools console:
 *
 *   localStorage.setItem('tkf.flags.enableOnboardingV2', 'true');
 *   localStorage.removeItem('tkf.flags.enableOnboardingV2');
 *
 * Reactively re-reads the value when the `storage` event fires (covers
 * other tabs of the same origin and manual edits via DevTools), so
 * toggling the flag does not require a page reload.
 *
 * This is intentionally separate from the backend-driven feature-flag
 * client in `@tankef/feature-flags`: it has zero network dependencies
 * and zero coordination with backend, which is exactly what we need
 * for a pre-rollout dev/QA gate.
 */
export function useLocalFlag(key: string, defaultValue = false): boolean {
  const [enabled, setEnabled] = useState(() => readFlag(key, defaultValue));

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== `${PREFIX}${key}`) return;
      setEnabled(readFlag(key, defaultValue));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, defaultValue]);

  return enabled;
}
